import { onBeforeUnmount, ref, watch, type ComputedRef, type Ref } from "vue";

/**
 * Attribute stamped on each rendered chip so the measurer can read its index back off
 * the DOM. Chips are rendered by Vuetify's `#chip` slot, so we can't hold refs to them.
 */
export const CHIP_INDEX_ATTR = "data-chip-index";

/** Attribute marking the "+N" overflow chip, which must never count against the fit. */
export const CHIP_OVERFLOW_ATTR = "data-chip-overflow";

/**
 * Space reserved at the end of the chip row for the "+N" chip itself, in pixels.
 * The overflow chip isn't in the DOM during a measure pass that currently shows every
 * chip, so its width has to be assumed. Slightly generous — under-reserving makes the
 * counter wrap out of view, over-reserving only drops one extra chip.
 */
const OVERFLOW_CHIP_RESERVE = 52;

type UseChipFitOptions = {
  /** Root element of the field; the chip row is looked up inside it. */
  container: Ref<HTMLElement | null>;
  /** `"fit"` measures; a number pins the visible count and skips measuring entirely. */
  visibleChips: ComputedRef<number | "fit">;
  /** Total selected items — re-measure whenever this changes. */
  selectedCount: ComputedRef<number>;
  /** Only measure while the field is actually rendering chips. */
  enabled: ComputedRef<boolean>;
};

/**
 * Decides how many chips fit on the single (nowrap) chip row of a select field.
 *
 * Measuring needs the chips laid out, but the count it produces then hides some of them —
 * so a pass runs in two phases: `measuring` is flipped on to force every chip visible,
 * widths are read after the browser has laid them out, then the resulting count is applied.
 * `measuring` is exposed so the template can render all chips during phase one.
 */
export function useChipFit({ container, visibleChips, selectedCount, enabled }: UseChipFitOptions) {
  /** Resolved number of chips to render before the "+N" chip. */
  const visibleChipCount = ref(typeof visibleChips.value === "number" ? visibleChips.value : 1);

  /** True while phase one is forcing all chips into the DOM to be measured. */
  const measuring = ref(false);

  let frame: number | null = null;
  let observer: ResizeObserver | null = null;

  const cancelFrame = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };

  /** Vuetify renders chips into `.v-field__input`; fall back to the root if absent. */
  const getChipRow = (): HTMLElement | null =>
    container.value?.querySelector<HTMLElement>(".v-field__input") ?? container.value;

  /**
   * Phase two: read the laid-out chips and count how many fit before the row's right edge.
   * Always keeps at least one chip so a single over-long value degrades to truncation
   * rather than collapsing into a bare "+1".
   */
  const applyMeasurement = () => {
    const row = getChipRow();
    if (!row) {
      measuring.value = false;
      return;
    }

    // `Array.from`, not spread — NodeListOf isn't iterable under this tsconfig's lib.
    // eslint-disable-next-line unicorn/prefer-spread
    const chips = Array.from(
      row.querySelectorAll<HTMLElement>(`[${CHIP_INDEX_ATTR}]:not([${CHIP_OVERFLOW_ATTR}])`)
    );

    const lastChip = chips.at(-1);
    if (!lastChip) {
      measuring.value = false;
      return;
    }

    const rowStyle = getComputedStyle(row);
    const rowRect = row.getBoundingClientRect();
    // The input's trailing padding already holds the append-inner icons; the chip row's
    // usable width stops there, not at the field border.
    const available = rowRect.width - Number.parseFloat(rowStyle.paddingRight || "0");

    // Reserve room for the "+N" chip unless every chip fits without one.
    const allFit = lastChip.getBoundingClientRect().right - rowRect.left <= available;

    if (allFit) {
      visibleChipCount.value = chips.length;
      measuring.value = false;
      return;
    }

    const budget = available - OVERFLOW_CHIP_RESERVE;
    let fitted = 0;
    for (const chip of chips) {
      if (chip.getBoundingClientRect().right - rowRect.left > budget) {
        break;
      }
      fitted += 1;
    }

    visibleChipCount.value = Math.max(1, fitted);
    measuring.value = false;
  };

  /** Phase one: show every chip, then measure on the next frame once layout has settled. */
  const measure = () => {
    if (visibleChips.value !== "fit" || !enabled.value) {
      return;
    }
    cancelFrame();
    measuring.value = true;
    frame = requestAnimationFrame(() => {
      frame = null;
      applyMeasurement();
    });
  };

  watch(
    visibleChips,
    (next) => {
      if (typeof next === "number") {
        cancelFrame();
        measuring.value = false;
        visibleChipCount.value = next;
        return;
      }
      measure();
    },
    { immediate: true }
  );

  watch([selectedCount, enabled], () => measure());

  watch(
    container,
    (el) => {
      observer?.disconnect();
      observer = null;
      if (!el || visibleChips.value !== "fit") {
        return;
      }
      observer = new ResizeObserver(() => measure());
      observer.observe(el);
      measure();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    cancelFrame();
    observer?.disconnect();
    observer = null;
  });

  return { visibleChipCount, measuring };
}
